import { CustomFieldModel } from '@base/modules/rest/custom-field/model/custom-field.model';
import { fromEvent, Observable, Subscription, switchMap } from 'rxjs';
import { CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { CustomFieldTypesEnum } from '@base/modules/rest/custom-field/enums/custom-field-types.enum';
import { DOWN_ARROW, ENTER, ESCAPE, UP_ARROW } from '@angular/cdk/keycodes';
import { ObjectRestServiceCreator } from '@base/modules/rest/object/object-rest-service-creator';
import { ChangeDetectorRef, ElementRef, inject, Renderer2 } from '@angular/core';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { ObjectDefinitionModel } from '@base/modules/rest/bbs-object/model/object-definition.model';
import { prepareLookupFilter } from '@views/secured/object/utils/composite-filters.util';
import { ObjectFindRequestModel } from '@base/modules/rest/object/model/object-find-request.model';
import { ObjectRestService } from '@base/modules/rest/object/object-rest.service';
import DxDataGrid, { CellPreparedEvent } from 'devextreme/ui/data_grid';
import { PaginationResponseModel } from '@base/model/common/pagination-response.model';
import { DxDataGridComponent } from 'devextreme-angular';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { NEW__ } from '@base/utils/table-data.util';
import { ItemsTableComponent } from '../items-table.component';
import { TableLookupDropdownItemComponent } from '../table-lookup-dropdown-item/table-lookup-dropdown-item.component';

export class ItemsTableLookupColumnHelper {
  lookupField: CustomFieldModel;
  searchValSubscription: Subscription;
  currentRowData: any;
  selectedDropdownIndex: number | undefined = -1;
  autocompleteList = [];
  isDropdownOpen = false;
  triggerOrigin: CdkOverlayOrigin;
  triggerRect: ClientRect;
  connectedPositions: ConnectedPosition = {
    originX: 'start', originY: 'bottom',
    overlayX: 'start', overlayY: 'top',
  };
  keyManager: ActiveDescendantKeyManager<TableLookupDropdownItemComponent>;

  private objectRestServiceCreator = inject(ObjectRestServiceCreator);
  private renderer: Renderer2 = inject(Renderer2);

  constructor(private itemsTable: ItemsTableComponent,
              private cdr: ChangeDetectorRef) {
  }

  addOverlayOrigin(e: CellPreparedEvent, field: CustomFieldModel): void {
    this.unsubscribeSearch();
    this.searchValSubscription = fromEvent(e.cellElement.firstChild, 'keyup')
      .pipe(
        debounceTime(300),
        map((event: any) => event.target.value),
        distinctUntilChanged(),
        tap(() => {
          this.lookupField = field;
          this.currentRowData = e;
        }),
        switchMap((val: string) => this.getRecordsBySearchString(val, field.lookupObject as ObjectDefinitionModel))
      )
      .subscribe((response) => {
          this.autocompleteList = response.content;

          // add dropdown overlay
          if (!this.autocompleteList.length) {
            return;
          }
          const overlayOriginElement = this.renderer.createElement('div');
          this.renderer.appendChild(e.cellElement, overlayOriginElement);
          const overlayOrigin: CdkOverlayOrigin = new CdkOverlayOrigin(new ElementRef(overlayOriginElement));
          this.triggerRect = overlayOrigin.elementRef.nativeElement.getBoundingClientRect();
          this.triggerOrigin = overlayOrigin;
          this.isDropdownOpen = true;

          // add key manager
          if (!this.keyManager) {
            this.keyManager = new ActiveDescendantKeyManager(this.itemsTable.dropdownItems).withWrap();
          }

          this.cdr.detectChanges();
        }
      );
  }

  addLookupButton(e: CellPreparedEvent, field: CustomFieldModel): void {
    const button = document.createElement('span');
    const icon = document.createElement('i');
    icon.className = 'dx-icon dx-icon-filter-operation-default';
    button.appendChild(icon);
    button.setAttribute('class', 'lookup-cell-button');
    button.style.visibility = !field?.readOnly && this.itemsTable.isEditMode ? 'visible' : 'hidden';
    button.addEventListener('click', () => {
      const columnIndex: number = e.columnIndex;
      const dataField: string = e.column.dataField;
      const key: string = e.row.key;
      const rowIndex: number = e.row.rowIndex;
      this.itemsTable.openLookupDialog(columnIndex, dataField, key, rowIndex);
    });
    e.cellElement.appendChild(button);
  }

  getRecordsBySearchString(searchVal: string, objectDefinition: ObjectDefinitionModel): Observable<PaginationResponseModel<any>> {
    const compositeFilter = searchVal ? prepareLookupFilter(objectDefinition, searchVal) : null;
    const data: ObjectFindRequestModel = {
      page: {page: 0, size: 5},
      filter: searchVal,
      compositeFilters: compositeFilter,
    };
    return this.restService(objectDefinition).find(data);
  }

  restService(objectDefinition: ObjectDefinitionModel): ObjectRestService {
    return this.objectRestServiceCreator.create(objectDefinition.name);
  }

  onKeyDown(event: KeyboardEvent, field: CustomFieldModel, cellPreparedEvent: CellPreparedEvent, dataGrid: DxDataGrid): void {
    const rows = this.dataGrid.instance.getVisibleRows().filter(row => row.rowType === 'data');
    const firstColIdx = this.dataGrid.instance.getVisibleColumns().findIndex(c => !!c.dataField);

    if (field?.type === CustomFieldTypesEnum.LOOKUP) {
      if (this.keyManager) {
        if (event.keyCode === ENTER) {
          if (this.keyManager.activeItem) {
            const selection = this.keyManager.activeItem.item;
            this.onDropdownElemClick(selection, event);
          } else {
            this.onKeyupEnterLookup(event, cellPreparedEvent);
          }
        } else {
          this.setIndexDirection(event);
          this.selectedDropdownIndex = this.keyManager.activeItemIndex;
        }
      } else {
        if (event.keyCode === ENTER) {
          this.onKeyupEnterLookup(event, cellPreparedEvent);
        }
      }
    }
    if (event.keyCode === ESCAPE && field?.type === CustomFieldTypesEnum.LOOKUP) {
      this.itemsTable.focusCell(cellPreparedEvent.rowIndex, cellPreparedEvent.columnIndex, dataGrid);
      this.closeDropdown();
    }
  }

  onKeyupEnterLookup(e, cellPreparedEvent: CellPreparedEvent): void {
    if (cellPreparedEvent.value === e.target.value) {
      return;
    }
    e.preventDefault();
    this.closeDropdown();
    const field = this.itemsTable.findFieldByDbName(cellPreparedEvent.column.dataField);
    if (field?.type === CustomFieldTypesEnum.LOOKUP) {
      const columnIndex: number = cellPreparedEvent.columnIndex;
      const dataField: string = cellPreparedEvent.column.dataField;
      const key: string = cellPreparedEvent.row.key;
      const rowIndex: number = cellPreparedEvent.row.rowIndex;
      const searchValue: string = e.target.value;
      this.itemsTable.openLookupDialog(columnIndex, dataField, key, rowIndex, searchValue);
    }
  }

  get dataGrid(): DxDataGridComponent {
    return this.itemsTable.dataGrid;
  }

  focusNextEditableCell(rowIndex: number, columnIndex: number): void {
    const columns: DxDataGridTypes.Column[] = this.dataGrid.instance.getVisibleColumns();
    const allRows = this.dataGrid.instance.getVisibleRows().filter(row => row.rowType === 'data');

    for (let i = rowIndex; i < allRows.length; i++) {
      const cellData: any = this.findRowEditableCell(columns, i, columnIndex);
      if (cellData) {
        this.itemsTable.focusCell(cellData.rowIndex, cellData.columnIndex, this.itemsTable.dataGrid.instance)
        return;
      }
      columnIndex = 0;
    }
  }

  findRowEditableCell(columns: DxDataGridTypes.Column[], rowIndex: number, columnIndex: number): any {
    for (let i = columnIndex; i < columns.length; i++) {
      if (columns[i].allowEditing) {
        return { rowIndex, columnIndex: i };
      }
    }
    return null;
  }

  setIndexDirection(event: KeyboardEvent): void {
    if (event.keyCode === UP_ARROW) {
      this.keyManager.setPreviousItemActive();
    }
    if (event.keyCode === DOWN_ARROW) {
      this.keyManager.setNextItemActive();
    }
  }

  onClicked(el: any, event?: MouseEvent | KeyboardEvent): void {
    event.stopPropagation();
    event.preventDefault();
  }

  onDropdownElemClick(el: any, event?: MouseEvent | KeyboardEvent): void {
    event.stopPropagation();
    this.itemsTable.lastCellData.value = el;
    this.restService(this.lookupField.lookupObject as ObjectDefinitionModel)
      .findOneSimple({id: el.id})
      .subscribe((response) => {
        const row: DxDataGridTypes.Row = getRowFromVisibleRows(this.dataGrid.instance, this.currentRowData.key);
        this.itemsTable.setCellValue(row.rowIndex, this.lookupField.name, response);
        // this.dataGrid.instance.saveEditData()
        //   .then(() => {
        //     if (this.itemsTable.inlineEditing) {
        //       this.autoAddNewRowFunction(this.currentRowData, row.rowIndex, this.currentRowData.column.dataField, this.itemsTable);
        //     }
        //     console.log(this.itemsTable.items)
        //   });
        setTimeout(() => {
          this.autoAddNewRowFunction(this.currentRowData, row.rowIndex, this.currentRowData.column.dataField, this.itemsTable);
          this.clearAfterOverlayClose();
        }, 10);
      });
  }

  autoAddNewRowFunction(rowObj: any, rowIndex: number, dataField: string, instance: ItemsTableComponent): void {
    if (rowObj.data[NEW__]) {
      const cell: {rowIndex: number, columnIndex: number} = getFocusCellIndexes(rowIndex, dataField, instance);
      instance.items[rowIndex][NEW__] = false;
      instance.addNewBlankRow(cell);
    } else {
      const columnIndex = instance.dataGrid.instance.getVisibleColumns().findIndex(c => c.dataField === dataField);
      this.focusNextEditableCell(rowIndex, columnIndex + 1);
    }
  }

  clearAfterOverlayClose(): void {
    this.autocompleteList = [];
    this.lookupField = null;
    this.selectedDropdownIndex = -1;
    this.isDropdownOpen = false;
    this.keyManager = null;
    this.unsubscribeSearch();
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
    this.keyManager = null;
    this.selectedDropdownIndex = -1;
  }

  unsubscribeSearch(): void {
    if (this.searchValSubscription) {
      this.searchValSubscription.unsubscribe();
    }
  }
}

export function getFocusCellIndexes(rowIndex: number, dataField: string, instance: ItemsTableComponent): {
  rowIndex: number;
  columnIndex: number
} {
  const visibleColumns = instance.dataGrid.instance.getVisibleColumns();
  const currentColumn = visibleColumns.find(c => c.dataField === dataField);
  let nextEditableColumn = visibleColumns.findIndex(c => c.allowEditing && c.visibleIndex > currentColumn.visibleIndex);

  if (nextEditableColumn === -1) {
    rowIndex++;
    nextEditableColumn = getFirstEditableColumn(instance.dataGrid.instance)
  }
  return {
    rowIndex: rowIndex,
    columnIndex: nextEditableColumn,
  };
}

export function getFirstEditableColumn(dataGrid: DxDataGrid): number {
  return dataGrid.getVisibleColumns().findIndex(c => c.allowEditing);
}

export function getRowFromVisibleRows(dataGrid: DxDataGrid, key: string): DxDataGridTypes.Row {
  const visibleRows = dataGrid.getVisibleRows();
  const row: DxDataGridTypes.Row = visibleRows.find(r => r.key === key);
  return row;
}
